import {
  useRef,
  useMemo,
  useState,
  useEffect,
  useContext,
  useCallback,
  createContext
} from 'react';

import {
  clientErrorTracker,
  useSoyuzClientConfig,
  useSoyuzFlag,
  useSoyuzLocales
} from '@spotahome/soyuz/client';

const DEFAULT_USE_RECAPTCHA_NET = false;
const DEFAULT_WIDGET_ID_DOM = 'recaptcha-widget';

function isBrowser() {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function getRecaptchaEnterprise() {
  const g = window?.grecaptcha;
  return g?.enterprise;
}

function createScriptSrc({ siteKey, useRecaptchaNet, language }) {
  const host = useRecaptchaNet
    ? 'https://www.recaptcha.net'
    : 'https://www.google.com';
  const qs = new URLSearchParams({ render: siteKey });

  if (language) {
    qs.set('hl', language);
  }

  return `${host}/recaptcha/enterprise.js?${qs.toString()}`;
}

function renderWidget({ api, siteKey, language, widgetIdDom }) {
  let container = document.getElementById(widgetIdDom);

  // Always create a fresh container to avoid cross-instance interference
  // This ensures each provider instance has its own clean widget
  if (container) {
    container.remove();
  }

  container = document.createElement('div');
  container.id = widgetIdDom;
  container.style.display = 'none';
  document.body.appendChild(container);

  const widgetId = api.render(container, {
    sitekey: siteKey,
    size: 'invisible',
    lang: language
  });

  return widgetId;
}

const ReCaptchaContext = createContext(null);

export function ReCaptchaProvider({
  children,
  nonce,
  useRecaptchaNet = DEFAULT_USE_RECAPTCHA_NET,
  widgetIdDom = DEFAULT_WIDGET_ID_DOM
}) {
  const [isReady, setIsReady] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState(null);
  const { recaptchaSiteKey } = useSoyuzClientConfig();
  const { locale: language } = useSoyuzLocales();
  const cancelledRef = useRef(false);
  const loadingPromiseRef = useRef(null);
  const widgetIdRef = useRef(null);
  const TenantRecaptchaFlag = useSoyuzFlag('TenantRecaptcha');

  useEffect(() => {
    cancelledRef.current = false;
    setIsReady(false);

    return () => {
      cancelledRef.current = true;
      setIsReady(false);

      // Reset refs synchronously before async DOM cleanup
      const widgetId = widgetIdRef.current;
      widgetIdRef.current = null;

      if (widgetId !== null && isBrowser()) {
        const api = getRecaptchaEnterprise();

        if (api?.reset) {
          try {
            api.reset(widgetId);
          } catch {
            // Silently fail cleanup if API is not available
          }
        }

        const container = document.getElementById(widgetIdDom);

        if (container) {
          container.remove();
        }
      }
    };
  }, [widgetIdDom]);

  useEffect(() => {
    if (!recaptchaSiteKey) {
      setIsError(true);
      setError(
        new Error(
          "Missing reCAPTCHA site key. Pass 'siteKey' to <ReCaptchaProvider />."
        )
      );
      setIsReady(false);
    } else {
      setIsError(false);
      setError(null);
    }
  }, [recaptchaSiteKey]);

  const handleScriptReady = useCallback(() => {
    if (cancelledRef.current) {
      return;
    }

    const api = getRecaptchaEnterprise();

    if (!api) {
      if (!cancelledRef.current) {
        setIsError(true);
        setError(new Error('reCAPTCHA Enterprise API not available'));
      }

      return;
    }

    // Prevent concurrent promise creation
    if (loadingPromiseRef.current) {
      return;
    }

    const readyPromise = new Promise((resolve, reject) => {
      api.ready(() => {
        if (cancelledRef.current) {
          resolve();
          return;
        }

        if (widgetIdRef.current !== null) {
          // Verify widget validity before setting isReady
          const container = document.getElementById(widgetIdDom);
          const isWidgetValid =
            container &&
            container.children.length > 0 &&
            api.getResponse(widgetIdRef.current) !== undefined;

          if (isWidgetValid) {
            setIsReady(true);
            resolve();
            return;
          }
          // Widget is stale, reset and re-render
          widgetIdRef.current = null;
          if (container) {
            container.remove();
          }
        }

        try {
          widgetIdRef.current = renderWidget({
            api,
            siteKey: recaptchaSiteKey,
            language,
            widgetIdDom
          });

          setIsReady(true);
          resolve();
        } catch (renderError) {
          if (!cancelledRef.current) {
            clientErrorTracker.error({
              error: renderError,
              source: 'GoogleReCaptcha-handleScriptReady'
            });

            setIsError(true);
            setError(
              renderError instanceof Error
                ? renderError
                : new Error('Failed to render reCAPTCHA widget.')
            );
          }

          reject(renderError);
        }
      });
    });

    loadingPromiseRef.current = readyPromise;
  }, [recaptchaSiteKey, language, widgetIdDom]);

  useEffect(() => {
    if (
      !recaptchaSiteKey ||
      cancelledRef.current ||
      TenantRecaptchaFlag.isOff()
    ) {
      return;
    }

    const api = getRecaptchaEnterprise();
    if (api) {
      handleScriptReady();
    }
  }, [recaptchaSiteKey, handleScriptReady]);

  const handleScriptError = useCallback(err => {
    if (cancelledRef.current) {
      return;
    }

    setIsError(true);
    setError(
      err instanceof Error
        ? err
        : new Error('Failed to load reCAPTCHA Enterprise script.')
    );
  }, []);

  const executeRecaptcha = useCallback(
    async action => {
      if (!recaptchaSiteKey) {
        throw new Error('Missing reCAPTCHA site key.');
      }

      if (cancelledRef.current) {
        return '';
      }

      if (loadingPromiseRef.current) {
        try {
          await loadingPromiseRef.current;
        } catch (err) {
          clientErrorTracker.error({
            error: err,
            source: 'GoogleReCaptcha-executeRecaptcha'
          });

          throw err;
        }
      }

      const api = getRecaptchaEnterprise();

      if (!api?.execute) {
        throw new Error('reCAPTCHA Enterprise API not available.');
      }

      if (!action) {
        throw new Error(
          'Missing reCAPTCHA action. Pass an action to executeRecaptcha(action).'
        );
      }

      // Verify widget is still valid before using widgetId
      if (widgetIdRef.current !== null) {
        const container = document.getElementById(widgetIdDom);
        const isWidgetValid =
          container &&
          container.children.length > 0 &&
          api.getResponse(widgetIdRef.current) !== undefined;

        if (isWidgetValid) {
          return api.execute(widgetIdRef.current, { action });
        }
        // Widget is stale, fall back to siteKey execution
        widgetIdRef.current = null;
      }

      return api.execute(recaptchaSiteKey, { action });
    },
    [recaptchaSiteKey, widgetIdDom]
  );

  const resetRecaptcha = useCallback(async () => {
    if (!recaptchaSiteKey) {
      throw new Error('Missing reCAPTCHA site key.');
    }

    if (cancelledRef.current) {
      return;
    }

    if (loadingPromiseRef.current) {
      try {
        await loadingPromiseRef.current;
      } catch (err) {
        clientErrorTracker.error({
          error: err,
          source: 'GoogleReCaptcha-resetRecaptcha'
        });

        throw err;
      }
    }

    const api = getRecaptchaEnterprise();

    if (!api?.reset) {
      throw new Error('reCAPTCHA Enterprise API not available.');
    }

    // Verify widget is still valid before using widgetId
    if (widgetIdRef.current !== null) {
      const container = document.getElementById(widgetIdDom);
      const isWidgetValid =
        container &&
        container.children.length > 0 &&
        api.getResponse(widgetIdRef.current) !== undefined;

      if (isWidgetValid) {
        api.reset(widgetIdRef.current);
        return;
      }
      // Widget is stale, reset ref and fall back to global reset
      widgetIdRef.current = null;
    }

    api.reset();
  }, [recaptchaSiteKey, widgetIdDom]);

  const scriptSrc = useMemo(
    () =>
      recaptchaSiteKey
        ? createScriptSrc({
            siteKey: recaptchaSiteKey,
            useRecaptchaNet,
            language
          })
        : '',
    [recaptchaSiteKey, useRecaptchaNet, language]
  );

  const value = useMemo(
    () => ({
      reCaptchaKey: recaptchaSiteKey,
      grecaptcha: isBrowser() ? getRecaptchaEnterprise() : undefined,
      isReady,
      isError,
      error,
      executeRecaptcha,
      resetRecaptcha
    }),
    [
      recaptchaSiteKey,
      isReady,
      isError,
      error,
      executeRecaptcha,
      resetRecaptcha
    ]
  );

  const apiExists = isBrowser() && getRecaptchaEnterprise() !== undefined;

  return (
    <ReCaptchaContext.Provider value={value}>
      {!apiExists && recaptchaSiteKey && TenantRecaptchaFlag.isOn() && (
        <script
          src={scriptSrc}
          defer
          async
          type="text/javascript"
          onLoad={handleScriptReady}
          onError={handleScriptError}
          nonce={nonce}
        />
      )}

      {children}
    </ReCaptchaContext.Provider>
  );
}

export function useReCaptcha(action) {
  const TenantRecaptchaFlag = useSoyuzFlag('TenantRecaptcha');
  const { executeRecaptcha, isReady, resetRecaptcha } =
    useContext(ReCaptchaContext);

  return {
    executeRecaptcha: async () => {
      if (TenantRecaptchaFlag.isOff()) {
        return null;
      }

      let recaptchaToken = null;

      if (isReady) {
        try {
          recaptchaToken = await executeRecaptcha(action);
        } catch (error) {
          clientErrorTracker.error({
            error,
            source: `ReCaptchaContext-useExecuteRecaptcha-${action}`
          });
        }
      }
      return recaptchaToken;
    },
    resetRecaptcha: async () => {
      if (TenantRecaptchaFlag.isOff()) {
        return;
      }

      try {
        await resetRecaptcha();
      } catch (error) {
        clientErrorTracker.error({
          error,
          source: `ReCaptchaContext-useResetRecaptcha-${action}`
        });
      }
    }
  };
}

export default ReCaptchaProvider;
